import React, {
  createContext, useState, useEffect, useContext
} from 'react';
import dynamicFederation from '../../federation/dynamicFederation';
import { Spin } from 'antd';
import styled from 'styled-components';
import { LoadingOutlined } from "@ant-design/icons";

const ModulesContext = createContext([]);
const loadedScripts = new Set();

const loadRemoteScript = async (moduleUrl) => {
  if (loadedScripts.has(moduleUrl)) {
    return;
  }

  await new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = moduleUrl;

    script.onload = () => {
      loadedScripts.add(moduleUrl);
      // Aguarda um pouco mais para garantir que o script foi totalmente executado
      // e o container foi exposto no window
      setTimeout(() => {
        resolve();
      }, 100);
    };

    script.onerror = (error) => {
      reject(new Error(`Failed to load script: ${moduleUrl}`));
    };
    
    document.head.appendChild(script);
  });
};

const loadModuleRoutes = async (module) => {
  try {
    await loadRemoteScript(module.moduleUrl);
    
    // Aguarda um pouco mais para garantir que o container está disponível
    await new Promise(resolve => setTimeout(resolve, 200));
    
    const routes = await dynamicFederation(module.containerName, './routes');

    return routes?.default || routes;
  } catch (error) {
    return null;
  }
};

const generatePageModules = async (modules) => {
  const pageModules = [];
  const processedContainers = new Map(); // Muda para Map para armazenar também a URL usada
  
  const existingRoutes = new Set(
    modules
      .filter(module => module.componentPage && module.componentPosition === 'page')
      .map(module => module.componentPage)
  );

  for (const module of modules) {
    try {
      // Verifica se o container já foi processado E se foi processado com a mesma URL
      const processedInfo = processedContainers.get(module.containerName);
      if (processedInfo && processedInfo.moduleUrl === module.moduleUrl) {
        continue;
      }
      
      processedContainers.set(module.containerName, { moduleUrl: module.moduleUrl });
      
      const routes = await loadModuleRoutes(module);
      
      if (routes?.pages) {
        Object.entries(routes.pages).forEach(([key, pageConfig]) => {
          if (!existingRoutes.has(pageConfig.path)) {
            const pageModule = {
              id: `${module.containerName}-${key}`,
              moduleUrl: module.moduleUrl,
              containerName: module.containerName,
              componentOrder: module.componentOrder || 1,
              componentPath: pageConfig.component,
              componentPosition: 'page',
              componentPage: pageConfig.path
            };
            pageModules.push(pageModule);
          }
        });
      }
    } catch (error) {
      // Error processing module
    }
  }
  return [...modules, ...pageModules];
};

const LoadingContainer = styled.div`
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #FFF;
`;

export const ModulesProvider = ({ children }) => {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const antIcon = <LoadingOutlined style={{ fontSize: 24, color: "#333" }} spin />;

  useEffect(() => {
    const fetchModules = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(`${	process.env.REACT_APP_MODULES_ENDPOINT }/modules.json`);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        const baseModules = Array.isArray(data.modules) ? data.modules : [];

        const allModules = await generatePageModules(baseModules);

        setModules(allModules);
      } catch (error) {
        setError(error);
        setModules([]);
      } finally {
        setLoading(false);
      }
    };

    fetchModules();
  }, []);

  if (loading) {
    return (
      <LoadingContainer>
        <Spin size="large" indicator={antIcon} />
      </LoadingContainer>
    );
  }

  if (error) {
    return (
      <div>
        Erro ao carregar módulos:
        {error.message}
      </div>
    );
  }

  return (
    <ModulesContext.Provider value={modules}>
      {children}
    </ModulesContext.Provider>
  );
};

export const ensureScriptLoaded = loadRemoteScript;

export const useModulesContext = () => {
  const context = useContext(ModulesContext);

  return Array.isArray(context) ? context : [];
};
